import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/http";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | timeout | error

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let tries = 0;
    let active = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (!active) return;
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setState("error"); return; }
      } catch (e) { console.debug("payment status poll retry", e); }
      tries += 1;
      if (tries > 10) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <Card className="rounded-2xl border bg-card p-8 shadow-sm max-w-sm w-full text-center" data-testid="payment-success-card">
        {state === "checking" && (
          <>
            <Loader2 className="h-10 w-10 text-primary mx-auto animate-spin mb-4" />
            <h1 className="font-heading font-bold text-xl">Confirming payment…</h1>
            <p className="text-muted-foreground text-sm mt-1">Hang tight, this only takes a moment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="h-12 w-12 text-primary mx-auto mb-4" data-testid="payment-paid-icon" />
            <h1 className="font-heading font-bold text-2xl">Payment received</h1>
            <p className="text-muted-foreground text-sm mt-1">The appointment is checked out and recorded as a sale.</p>
            <Link to="/calendar"><Button className="rounded-lg mt-6 w-full" data-testid="payment-back-calendar">Back to calendar</Button></Link>
          </>
        )}
        {(state === "timeout" || state === "error") && (
          <>
            <XCircle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <h1 className="font-heading font-bold text-xl">{state === "timeout" ? "Still processing" : "Something went wrong"}</h1>
            <p className="text-muted-foreground text-sm mt-1">If you were charged, the sale will appear shortly. Otherwise try again.</p>
            <Link to="/calendar"><Button variant="outline" className="rounded-lg mt-6 w-full">Back to calendar</Button></Link>
          </>
        )}
      </Card>
    </div>
  );
}
